import React, { useState } from 'react';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Checkbox,
  IconButton,
  Typography,
  RadioGroup,
  Radio,
} from 'design-system';
import theme, { makeStyles } from 'design-system/product/theme';
import { TextInput } from 'react-scale/components/design-system';
import _ from 'lodash';
// eslint-disable-next-line restrict-imports/restrict-imports
import CloseIcon from '@material-ui/icons/Close';

interface TextBasedSpeedAuditFlagModalProps {
  isOpen: boolean;
  fields: string[];
  onFlag: (fields: string[], prompt?: string) => void;
  onRequestClose: () => void;
}

const TextBasedSpeedAuditFlagModal: React.FC<TextBasedSpeedAuditFlagModalProps> = ({
  isOpen,
  fields,
  onFlag,
  onRequestClose,
}) => {
  const classes = useStyles();
  const [prompt, setPrompt] = useState('');
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [mode, setMode] = useState('0');

  return (
    <Dialog maxWidth="md" open={isOpen} onClose={onRequestClose} fullWidth>
      <DialogTitle>
        <div className={classes.close}>
          <IconButton aria-label="close" size="small" onClick={onRequestClose}>
            <CloseIcon />
          </IconButton>
        </div>
        <Typography className={classes.header}>LLM Task Flag</Typography>
        <Typography className={classes.subtitle}>Use an LLM to flag fields</Typography>
      </DialogTitle>

      <DialogContent>
        <div className={classes.container}>
          <div className={classes.flagGroup}>
            Select which fields to flag:
            {fields.map(field => (
              <Checkbox
                checkBoxColor={theme.colors.periwinkle100}
                key={field}
                label={field}
                checked={selectedFields.includes(field)}
                onChange={() => {
                  const tempFields = _.cloneDeep(selectedFields);
                  if (tempFields.includes(field)) {
                    setSelectedFields(tempFields.filter(f => f !== field));
                  } else {
                    tempFields.push(field);
                    setSelectedFields(tempFields);
                  }
                }}
              />
            ))}
          </div>
          Field Flag Options:
          <div className={classes.flagGroup}>
            <RadioGroup value={mode} onChange={(value: string) => setMode(value)}>
              <Radio
                className={classes.checkbox}
                key="gibberish"
                value="gibberish"
                label="Gibberish Validator"
              />
              <Radio
                className={classes.checkbox}
                key="custom"
                value="custom"
                label="Custom Prompt"
              />
            </RadioGroup>
          </div>
          {mode === 'custom' ? (
            <div className={classes.container}>
              <span>
                Prompts can reference any number of fields for flagging. To reference a field in a
                prompt, write it as {'{{Field Name}}'}
              </span>
              <TextInput
                fullWidth
                multiline
                size="large"
                label={'Prompt'}
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                placeholder={
                  'Is the text below in the quotes written well, meaning that it was written by a native English speaker and has few spelling and grammatical errors (yes/no)? "{{Prompt}}"'
                }
              />
              <Button
                className={classes.completeButton}
                disabled={selectedFields.length === 0}
                onClick={() => {
                  onFlag(selectedFields, prompt);
                }}
              >
                Flag with Prompt
              </Button>
            </div>
          ) : (
            <div style={{ width: '100%' }}>
              <Button
                className={classes.completeButton}
                disabled={selectedFields.length === 0}
                onClick={() => onFlag(selectedFields)}
              >
                Flag with Gibberish Validator
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  close: {
    position: 'absolute',
    right: 8,
    top: 8,
    color: () => 'gray',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  subtitle: {
    fontSize: '14px',
    fontWeight: 400,
    color: 'gray',
  },
  flagGroup: {
    display: 'flex',
    flexDirection: 'column',
  },
  checkbox: {
    '&.Mui-checked': {
      color: 'rgb(25, 118, 210, 1)',
    },
  },
  completeButton: {
    padding: '6px 16px !important',
    color: 'white',
    background: 'rgb(25, 118, 210, 1)',
    '&:hover': {
      background: 'rgb(25, 118, 210, 0.75)',
    },
  },
}));

export default TextBasedSpeedAuditFlagModal;
